/* Upstream shell behavior: color mode engine, mobile menu focus trap,
   reading progress, FAQ filter. Ported verbatim from the static build.
   The color mode resolution order here must stay identical to the inline
   boot script in layout/theme.liquid or the page flashes on load. */
"use strict";

(function initializeUpstreamShell() {
  var storageKey = "upstream-color-mode";
  var root = document.documentElement;
  var allowedModes = ["light", "dark"];

  function storedMode() {
    try {
      var value = localStorage.getItem(storageKey);
      return allowedModes.indexOf(value) >= 0 ? value : "";
    } catch {
      return "";
    }
  }

  function preferredMode() {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }

  function applyMode(mode) {
    root.dataset.colorMode = mode;
    root.setAttribute("data-color-mode", mode);
    root.style.colorScheme = mode;

    document.querySelectorAll("[data-color-mode-option]").forEach(function updateButton(button) {
      button.setAttribute(
        "aria-pressed",
        button.getAttribute("data-color-mode-option") === mode ? "true" : "false"
      );
    });

    /* The single toggle in the desktop header. It is one button representing
       "dark mode", so its accessible name stays constant and aria-pressed
       carries the state, which is the correct pattern for a binary toggle.
       Changing the label text instead would make a screen reader announce a
       different control each time it was used. The two-option segmented
       control above is still used inside the phone menu, where there is room
       for labels and the drawer reads as a settings surface. */
    document.querySelectorAll("[data-color-mode-toggle]").forEach(function updateToggle(button) {
      button.setAttribute("aria-pressed", mode === "dark" ? "true" : "false");
    });
  }

  function persistMode(mode) {
    try {
      localStorage.setItem(storageKey, mode);
    } catch {
      return;
    }
  }

  root.classList.add("js");
  applyMode(storedMode() || preferredMode());

  function ready() {
    var openButton = document.querySelector("[data-menu-open]");
    var menu = document.querySelector("#mobile-menu");
    var closeButton = document.querySelector("[data-menu-close]");
    var readingProgress = document.querySelector("[data-reading-progress] span");

    if (readingProgress) {
      var updateReadingProgress = function () {
        var rootHeight = document.documentElement.scrollHeight - window.innerHeight;
        var progress = rootHeight > 0 ? Math.min(100, Math.max(0, (window.scrollY / rootHeight) * 100)) : 0;
        readingProgress.style.transform = "scaleX(" + (progress / 100) + ")";
      };
      updateReadingProgress();
      window.addEventListener("scroll", updateReadingProgress, { passive: true });
      window.addEventListener("resize", updateReadingProgress);
    }
    var lastFocused = null;

    applyMode(root.dataset.colorMode || preferredMode());

    document.querySelectorAll("[data-color-mode-toggle]").forEach(function bindToggle(button) {
      button.addEventListener("click", function flipMode() {
        var mode = root.dataset.colorMode === "dark" ? "light" : "dark";
        applyMode(mode);
        document.dispatchEvent(new CustomEvent("upstream:modechange", {
          detail: { mode: mode, control: button }
        }));
        persistMode(mode);
      });
    });

    document.querySelectorAll("[data-color-mode-option]").forEach(function bindMode(button) {
      button.addEventListener("click", function selectMode() {
        var mode = button.getAttribute("data-color-mode-option");
        applyMode(mode);
        document.dispatchEvent(new CustomEvent("upstream:modechange", {
          detail: { mode: mode, control: button }
        }));
        persistMode(mode);
      });
    });

    var faqSearch = document.querySelector("[data-faq-search]");
    if (faqSearch) {
      var faqEntries = Array.from(document.querySelectorAll("[data-faq-entry]"));
      var faqCategories = Array.from(document.querySelectorAll("[data-faq-category]"));
      var faqResults = document.querySelector("[data-faq-results]");
      var faqEmpty = document.querySelector("[data-faq-empty]");
      var faqClear = document.querySelector("[data-faq-clear]");

      function updateFaqResults() {
        var term = faqSearch.value.trim().toLocaleLowerCase("en-US");
        var visibleCount = 0;

        faqEntries.forEach(function filterEntry(entry) {
          var matches = !term || entry.dataset.searchText.includes(term);
          entry.hidden = !matches;
          if (matches) visibleCount += 1;
        });

        faqCategories.forEach(function filterCategory(category) {
          category.hidden = !category.querySelector("[data-faq-entry]:not([hidden])");
        });

        if (faqEmpty) faqEmpty.hidden = visibleCount !== 0;
        if (faqResults) {
          faqResults.textContent = `${visibleCount} ${visibleCount === 1 ? "question" : "questions"} shown.`;
        }
      }

      faqSearch.addEventListener("input", updateFaqResults);
      faqSearch.addEventListener("search", updateFaqResults);
      if (faqClear) {
        faqClear.addEventListener("click", function clearFaqSearch() {
          faqSearch.value = "";
          updateFaqResults();
          faqSearch.focus();
        });
      }
    }

    var contactForm = document.querySelector("[data-contact-form]");
    if (contactForm) {
      var contactStartedAt = contactForm.querySelector("[data-contact-started-at]");
      var contactDraftLink = contactForm.querySelector("[data-contact-draft-link]");
      var contactStatus = contactForm.querySelector("[data-contact-status]");
      var contactEmail = contactForm.dataset.contactEmail;
      var hasEndpoint = contactForm.hasAttribute("action");
      if (contactStartedAt) contactStartedAt.value = String(Date.now());

      function contactMailto() {
        var values = new FormData(contactForm);
        var business = String(values.get("business") || "").trim();
        var subject = business ? `Website inquiry from ${business}` : "Website inquiry";
        var body = [
          `Name: ${String(values.get("name") || "").trim() || "Not provided"}`,
          `Business: ${business || "Not provided"}`,
          `Reply email: ${String(values.get("email") || "").trim() || "Not provided"}`,
          `Package interest: ${String(values.get("package") || "").trim() || "Not selected"}`,
          "",
          "Project context:",
          String(values.get("message") || "").trim() || "Not provided"
        ].join("\n");
        return `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }

      function updateContactDraft() {
        if (contactDraftLink) contactDraftLink.href = contactMailto();
      }

      contactForm.addEventListener("input", updateContactDraft);
      updateContactDraft();

      function markContactReceived() {
        var controls = contactForm.querySelectorAll("input, textarea, select, button");
        for (var i = 0; i < controls.length; i += 1) controls[i].disabled = true;
        contactForm.setAttribute("data-contact-received", "");
        if (contactStatus) {
          contactStatus.textContent = "Received. I reply within one business day.";
        }
      }

      // The Worker answers a native (no-JS) post with a 303 back to this page. Landing
      // here with that flag means the inquiry already arrived, so confirm it rather
      // than presenting an empty form the visitor may fill a second time.
      var landedReceived = false;
      try {
        landedReceived = new URLSearchParams(window.location.search).get("lead") === "received";
      } catch (err) {
        landedReceived = false;
      }
      if (landedReceived) markContactReceived();

      if (!hasEndpoint) {
        contactForm.addEventListener("submit", function prepareContactEmail(event) {
          event.preventDefault();
          if (!contactForm.checkValidity()) {
            contactForm.reportValidity();
            return;
          }
          var honeypot = contactForm.elements.website_confirm;
          if (honeypot && honeypot.value) {
            if (contactStatus) contactStatus.textContent = "The inquiry could not be prepared. Email directly instead.";
            return;
          }
          var started = Number(contactStartedAt?.value || 0);
          if (started && Date.now() - started < 750) {
            if (contactStatus) contactStatus.textContent = "Please review the inquiry, then submit again.";
            return;
          }
          window.location.href = contactMailto();
        });
      } else if (!landedReceived) {
        // An endpoint is configured. The form's own action/method already works with
        // JavaScript off; this upgrades it to a same-page submit. On any transport
        // failure it hands back the prepared email, so a dead endpoint still cannot
        // lose an inquiry.
        contactForm.addEventListener("submit", function postContactInquiry(event) {
          event.preventDefault();
          if (!contactForm.checkValidity()) {
            contactForm.reportValidity();
            return;
          }
          var submit = contactForm.querySelector('button[type="submit"]');
          var restoreLabel = submit ? submit.textContent : "";
          if (submit) {
            submit.disabled = true;
            submit.textContent = "Sending";
          }
          if (contactStatus) contactStatus.textContent = "Sending your inquiry.";
          var values = {};
          new FormData(contactForm).forEach(function collect(value, key) { values[key] = value; });
          fetch(contactForm.getAttribute("action"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(values)
          }).then(function readBody(response) {
            return response.json().catch(function noBody() { return {}; }).then(function withBody(payload) {
              return { response: response, payload: payload };
            });
          }).then(function settle(result) {
            if (result.response.ok && result.payload.ok === true) {
              markContactReceived();
              return;
            }
            var message = result.payload.errors && result.payload.errors.length
              ? result.payload.errors[0]
              : "That inquiry could not be sent.";
            // A 400 is the visitor's input, not a broken endpoint: show it so they
            // can fix it, rather than masking a real error with a mailto.
            if (result.response.status === 400) {
              if (submit) { submit.disabled = false; submit.textContent = restoreLabel; }
              if (contactStatus) contactStatus.textContent = message;
              return;
            }
            throw new Error(message);
          }).catch(function fallback() {
            if (submit) { submit.disabled = false; submit.textContent = restoreLabel; }
            if (contactStatus) {
              contactStatus.textContent = "That did not send. Opening a prepared email instead so nothing is lost.";
            }
            window.location.href = contactMailto();
          });
        });
      }
    }

    if (!openButton || !menu || !closeButton) return;

    function openMenu() {
      lastFocused = document.activeElement;
      menu.hidden = false;
      openButton.setAttribute("aria-expanded", "true");
      document.body.classList.add("menu-open");
      closeButton.focus();
    }

    function closeMenu() {
      menu.hidden = true;
      openButton.setAttribute("aria-expanded", "false");
      document.body.classList.remove("menu-open");
      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
    }

    openButton.addEventListener("click", openMenu);
    closeButton.addEventListener("click", closeMenu);
    menu.querySelectorAll("a").forEach(function bindMenuLink(link) {
      link.addEventListener("click", closeMenu);
    });

    menu.addEventListener("keydown", function containFocus(event) {
      if (event.key === "Escape") {
        closeMenu();
        return;
      }

      if (event.key !== "Tab") return;
      var focusable = Array.from(
        menu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])')
      ).filter(function visible(element) {
        return element.getAttribute("aria-hidden") !== "true";
      });
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", ready, { once: true });
  } else {
    ready();
  }
}());
